import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const empty = { category_id: "", name: "", description: "", price: 0, image_url: "", is_veg: true, is_available: true, is_best_seller: false, is_chef_special: false, tags: [], modifier_groups: [] };

// Shrink a photo before upload: phone camera shots are 3-8MB but a menu card only
// needs ~1200px — canvas re-encode to JPEG lands around 100-300KB (server caps
// uploads at 800KB). Files the canvas can't decode fall through untouched so the
// server's own type/size checks produce the real error message.
async function compressImage(file, maxDim = 1200, quality = 0.82) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");

  // Upload path fills the SAME image_url field the link option uses — one field,
  // two ways to set it, so nothing downstream changes.
  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const blob = await compressImage(file);
      const type = blob.type || file.type || "image/jpeg";
      const r = await api.post("/admin/menu/upload-image", blob, { headers: { "Content-Type": type } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success(`Photo uploaded (${Math.max(1, Math.round((r.data.size || blob.size) / 1024))}KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — try a JPG/PNG under 5MB");
    } finally {
      setUploading(false);
    }
  };

  const load = () => { api.get("/admin/menu").then((r) => setItems(r.data)); api.get("/admin/categories").then((r) => setCats(r.data)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.id) await api.patch(`/admin/menu/${form.id}`, form);
      else await api.post("/admin/menu", form);
      toast.success("Saved"); setOpen(false); setForm(empty); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/menu/${id}`); load(); };

  // --- modifier-group builder ---
  const mg = () => form.modifier_groups || [];
  const addGroup = () => setForm((f) => ({ ...f, modifier_groups: [...(f.modifier_groups || []), { name: "", type: "single", required: false, min: 0, max: 0, options: [] }] }));
  const updateGroup = (gi, patch) => setForm((f) => { const g = [...(f.modifier_groups || [])]; g[gi] = { ...g[gi], ...patch }; return { ...f, modifier_groups: g }; });
  const deleteGroup = (gi) => setForm((f) => ({ ...f, modifier_groups: (f.modifier_groups || []).filter((_, i) => i !== gi) }));
  const addOption = (gi) => setForm((f) => { const g = [...(f.modifier_groups || [])]; g[gi] = { ...g[gi], options: [...(g[gi].options || []), { name: "", price_delta: 0 }] }; return { ...f, modifier_groups: g }; });
  const updateOption = (gi, oi, patch) => setForm((f) => { const g = [...(f.modifier_groups || [])]; const opts = [...(g[gi].options || [])]; opts[oi] = { ...opts[oi], ...patch }; g[gi] = { ...g[gi], options: opts }; return { ...f, modifier_groups: g }; });
  const deleteOption = (gi, oi) => setForm((f) => { const g = [...(f.modifier_groups || [])]; g[gi] = { ...g[gi], options: (g[gi].options || []).filter((_, i) => i !== oi) }; return { ...f, modifier_groups: g }; });

  // Client-side search over the loaded list — matches name, description, or category.
  const catName = (id) => cats.find((c) => c.id === id)?.name || "";
  const shown = items.filter((it) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [it.name, it.description, catName(it.category_id)].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">Catalog</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Menu Items</h1>
        </div>
        <Button data-testid="menu-add-btn" onClick={() => { setForm({ ...empty, category_id: cats[0]?.id || "" }); setOpen(true); }} className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
      </header>

      <div className="mb-3 relative max-w-sm">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden />
        <Input data-testid="menu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="pl-9" />
      </div>

      <div className="qd-admin-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr><th className="text-left p-3">Item</th><th className="text-left p-3">Category</th><th className="text-left p-3">Price</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-gray-400 text-sm">{q ? "No items match your search." : "No items yet."}</td></tr>
            )}
            {shown.map((it) => (
              <tr key={it.id} data-testid={`menu-row-${it.id}`} className="border-t border-gray-100">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <img src={it.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                    <div>
                      <div className="font-medium">{it.name}</div>
                      <div className="text-xs text-gray-400">{it.is_veg ? "Veg" : "Non-veg"}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-gray-600">{cats.find((c) => c.id === it.category_id)?.name || "—"}</td>
                <td className="p-3 font-semibold">₹{it.price}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${it.is_available ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{it.is_available ? "Available" : "Hidden"}</span>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`menu-edit-${it.id}`} onClick={() => { setForm(it); setOpen(true); }} className="p-1.5 text-gray-500 hover:text-gray-800"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`menu-delete-${it.id}`} onClick={() => del(it.id)} className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Input data-testid="form-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" />
            <Textarea data-testid="form-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-2" />
            <Input data-testid="form-price" type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || "0") })} />
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
              <SelectTrigger data-testid="form-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="col-span-2 flex items-center gap-2">
              {form.image_url && (
                <img src={form.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 border border-gray-200"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              <Input data-testid="form-image" placeholder="Image URL (or upload →)" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="flex-1" />
              <label data-testid="form-image-upload" className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium cursor-pointer select-none ${uploading ? "opacity-60 pointer-events-none" : "hover:bg-gray-50"}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Upload className="w-4 h-4" aria-hidden />}
                {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                       onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="form-veg" checked={form.is_veg} onCheckedChange={(v) => setForm({ ...form, is_veg: v })} /> Vegetarian</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="form-avail" checked={form.is_available} onCheckedChange={(v) => setForm({ ...form, is_available: v })} /> Available</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="form-best" checked={form.is_best_seller} onCheckedChange={(v) => setForm({ ...form, is_best_seller: v })} /> Best Seller</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="form-chef" checked={form.is_chef_special} onCheckedChange={(v) => setForm({ ...form, is_chef_special: v })} /> Chef's Special</label>
          </div>

          {/* Customization builder */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-semibold">Customization</div>
                <div className="text-xs text-gray-400">Option groups like "Choose bread" or "Add-ons", with prices.</div>
              </div>
              <Button type="button" data-testid="mg-add-group" onClick={addGroup} variant="outline" className="h-8 text-xs rounded-lg"><Plus className="w-3.5 h-3.5 mr-1" /> Add group</Button>
            </div>
            <div className="space-y-3">
              {mg().map((g, gi) => (
                <div key={gi} data-testid={`mg-group-${gi}`} className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Input placeholder="Group name (e.g. Choose bread)" value={g.name} onChange={(e) => updateGroup(gi, { name: e.target.value })} className="flex-1 h-8 bg-white" />
                    <button type="button" data-testid={`mg-del-group-${gi}`} onClick={() => deleteGroup(gi)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mb-2 text-xs text-gray-600">
                    <select value={g.type} onChange={(e) => updateGroup(gi, { type: e.target.value })} className="h-8 rounded border border-gray-200 px-2 bg-white">
                      <option value="single">Single-select (radio)</option>
                      <option value="multi">Multi-select (checkbox)</option>
                    </select>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!g.required} onChange={(e) => updateGroup(gi, { required: e.target.checked })} /> Required</label>
                    {g.type === "multi" && (<>
                      <label className="inline-flex items-center gap-1">Min <input type="number" min="0" value={g.min || 0} onChange={(e) => updateGroup(gi, { min: parseInt(e.target.value) || 0 })} className="w-12 h-8 rounded border border-gray-200 px-1 bg-white" /></label>
                      <label className="inline-flex items-center gap-1">Max <input type="number" min="0" value={g.max || 0} onChange={(e) => updateGroup(gi, { max: parseInt(e.target.value) || 0 })} className="w-12 h-8 rounded border border-gray-200 px-1 bg-white" /></label>
                    </>)}
                  </div>
                  <div className="space-y-1.5">
                    {(g.options || []).map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <Input placeholder="Option name" value={o.name} onChange={(e) => updateOption(gi, oi, { name: e.target.value })} className="flex-1 h-8 bg-white" />
                        <div className="inline-flex items-center gap-1 text-xs text-gray-500 shrink-0">+₹<Input type="number" value={o.price_delta || 0} onChange={(e) => updateOption(gi, oi, { price_delta: parseFloat(e.target.value) || 0 })} className="w-16 h-8 bg-white" /></div>
                        <button type="button" onClick={() => deleteOption(gi, oi)} className="p-1 text-gray-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <Button type="button" data-testid={`mg-add-opt-${gi}`} onClick={() => addOption(gi)} variant="ghost" className="h-7 text-xs text-[var(--qd-primary)] hover:text-[var(--qd-primary-dark)] px-2">+ Add option</Button>
                  </div>
                </div>
              ))}
              {mg().length === 0 && <div className="text-xs text-gray-400">No customization yet. Add a group like "Choose bread" or "Add-ons".</div>}
            </div>
          </div>

          <Button data-testid="form-save" onClick={save} className="mt-4 bg-[var(--qd-charcoal)] hover:bg-black">Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
